import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { Name } from '../enums/name.enum';
import { ISPUtil } from '../../shared/utils/isp.util';
import { AppConfigService } from '../config/app-config.service';

@Injectable({
  providedIn: 'root'
})
export class CookieService {

  // =========================
  // DEPENDENCIES
  // =========================

  private readonly document = inject(DOCUMENT);
  private readonly environment = inject(AppConfigService).getConfig();

  // =========================
  // SET COOKIE
  // =========================

  set(
    name: string,
    value: unknown,
    path: string = '/',
    days: number = 365
  ): void {

    this.delete(name, path);

    let expires = '';

    if (days) {
      const date = new Date();

      date.setTime(
        date.getTime() + days * 24 * 60 * 60 * 1000
      );

      expires = `; expires=${date.toUTCString()}`;
    }

    const encryptedValue = this.securityOperation(
      'encrypt',
      value
    );

    this.document.cookie =
      `${name}=${encryptedValue || ''}${expires}; path=${path}; SameSite=Lax`;
  }

  // =========================
  // GET COOKIE
  // =========================

  get(name: string): string | null {

    if (!name) {
      return null;
    }

    const nameEQ = `${name}=`;

    const cookies = this.document.cookie.split(';');

    for (let cookie of cookies) {

      cookie = cookie.trim();

      if (cookie.startsWith(nameEQ)) {

        const encryptedValue = cookie.substring(nameEQ.length);

        const decryptedValue = this.securityOperation(
          'decrypt',
          encryptedValue
        );

        // invalid / tampered cookie
        if (!decryptedValue) {

          this.delete(Name.AUTH_TOKEN);

          window.location.reload();

          return null;
        }

        return decryptedValue;
      }
    }

    return null;
  }

  // =========================
  // DELETE COOKIE
  // =========================

  delete(
    name: string,
    path: string = '/'
  ): void {

    this.document.cookie =
      `${name}=; Path=${path}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }

  // =========================
  // CLEAR ALL
  // =========================

  clearAll(): void {

    const cookies = this.document.cookie.split(';');

    for (const cookie of cookies) {

      const eqPos = cookie.indexOf('=');

      const cookieName =
        eqPos > -1
          ? cookie.substring(0, eqPos).trim()
          : cookie.trim();

      this.delete(cookieName);
    }
  }

  // =========================
  // ENCRYPT / DECRYPT
  // =========================

  private securityOperation(
    operation: 'encrypt' | 'decrypt',
    text: unknown
  ): string | null {

    try {

      const secretKey = this.environment.secretKey;

      if (operation === 'encrypt') {

        return ISPUtil.encrypt(
          secretKey,
          String(text)
        );
      }

      return ISPUtil.decrypt(
        secretKey,
        String(text)
      );

    } catch (error) {

      sessionStorage.clear();

      return null;
    }
  }
}